/**
 * ExerciseSeriesPlayer — plays a sequence of exercises one at a time.
 *
 * Given a list of exercises (or filter params that it will fetch), the
 * player renders the current item with its prompt / display / options,
 * shows immediate feedback, tracks score, and at the end shows a summary
 * with a "Recommencer" / "Retour" action.
 *
 * Each answer is POSTed via `coursesApi.logAttempt` (auth only,
 * fire-and-forget) so the user's skill progression is updated.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, X, ArrowRight, Sparkles, RefreshCcw, Trophy, Target,
  Volume2, Pause,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { coursesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { RecordAndCompare } from './RecordAndCompare';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('http') ? u : `${API_BASE}${u || ''}`);
const pickFr = (s) => (typeof s === 'string' ? s : s?.fr || '');

// --- Deterministic per-exercise option shuffle --------------------------
// The backend stores QCM `options` with the correct answer biased to the
// first position (legacy generator). We reorder them client-side at display
// time so the user can't memorise positions, while keeping the shuffle
// STABLE across re-renders of the same exercise (otherwise the UI would
// jiggle on every state change).
const _hashKey = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
};
const _seededShuffle = (arr, seed) => {
  // Mulberry32 PRNG → Fisher-Yates
  const out = arr.slice();
  let s = seed >>> 0;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    const j = Math.floor(r * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const ExerciseSeriesPlayer = ({
  exercises = null,         // pre-loaded list (optional)
  filter = null,            // {section, skill, ruleId, lessonId, difficulty, limit}
  testidPrefix = 'series',
  onExit = null,            // back button callback
  onComplete = null,        // ({correct,wrong,total,percent,answeredIds}) on finish
}) => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState(exercises || []);
  const [loading, setLoading] = useState(!exercises);
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState(null);      // {value, correct}
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [done, setDone] = useState(false);

  // Single shared audio element so navigation between questions stops any
  // playback already in progress.
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioPlaying(false);
  };
  const playAudio = (url) => {
    if (!url) return;
    if (!audioRef.current) audioRef.current = new Audio();
    if (audioPlaying) { stopAudio(); return; }
    audioRef.current.src = absUrl(url);
    audioRef.current.onended = () => setAudioPlaying(false);
    audioRef.current.onerror = () => setAudioPlaying(false);
    audioRef.current.play().catch(() => setAudioPlaying(false));
    setAudioPlaying(true);
  };
  // Stop any playback when changing question / unmounting
  useEffect(() => () => stopAudio(), []);
  useEffect(() => { stopAudio(); }, [idx]); // eslint-disable-line

  useEffect(() => {
    if (exercises) { setItems(exercises); setLoading(false); return; }
    if (!filter) return;
    let cancel = false;
    setLoading(true);
    coursesApi
      .listExercises({ ...filter, limit: filter.limit || 10 })
      .then((r) => {
        if (cancel) return;
        const list = r.data?.items || [];
        // Take the first `limit` then shuffle deterministically — keep
        // order stable across reloads by sorting by id.
        setItems(list);
        setIdx(0);
        setAnswer(null);
        setScore({ correct: 0, total: 0 });
        setDone(false);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [exercises, JSON.stringify(filter)]); // eslint-disable-line

  const current = items[idx] || null;

  // Stable random shuffle of options using exercise id as seed. The hook
  // MUST be called before any early-return so React can preserve hook
  // order. The shuffle is keyed by exercise id and matches via value (not
  // by index), so correction stays exact.
  const rawOptions = current?.options || [];
  const options = useMemo(
    () => _seededShuffle(rawOptions, _hashKey(String(current?.id || ''))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current?.id, rawOptions.length],
  );

  const onChoose = (opt) => {
    if (answer || !current) return;
    const correct = opt.value === current.correctAnswer;
    setAnswer({ value: opt.value, correct });
    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    if (isAuthenticated) {
      coursesApi.logAttempt({
        exerciseId: current.id,
        lessonId: current.lessonId,
        section: current.section,
        skill: current.skill,
        ruleId: current.ruleId,
        answer: opt.value,
        isCorrect: correct,
      }).catch(() => {});
    }
  };

  const next = () => {
    setAnswer(null);
    if (idx + 1 < items.length) {
      setIdx(idx + 1);
    } else {
      setDone(true);
      if (onComplete) {
        const total = items.length;
        const percent = total ? Math.round((score.correct / total) * 100) : 0;
        try {
          onComplete({
            correct: score.correct,
            wrong: total - score.correct,
            total,
            percent,
            answeredIds: items.map((x) => x.id),
          });
        } catch { /* ignore */ }
      }
    }
  };

  const restart = () => {
    setIdx(0);
    setAnswer(null);
    setScore({ correct: 0, total: 0 });
    setDone(false);
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-surface-elevated/30 p-8 text-center text-sm text-text-muted"
           data-testid={`${testidPrefix}-loading`}>
        Chargement de la série…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-lg border border-border bg-surface-elevated/30 p-8 text-center text-sm text-text-muted"
           data-testid={`${testidPrefix}-empty`}>
        Aucun exercice disponible pour ce niveau pour le moment.
      </div>
    );
  }

  // ---------- End-of-series summary ----------
  if (done) {
    const pct = score.total ? Math.round((score.correct / score.total) * 100) : 0;
    const verdict =
      pct >= 90 ? { label: 'Excellent', cls: 'text-emerald-300' } :
      pct >= 70 ? { label: 'Très bien', cls: 'text-emerald-300' } :
      pct >= 50 ? { label: 'À consolider', cls: 'text-amber-300' } :
                  { label: 'À revoir', cls: 'text-rose-300' };
    return (
      <div
        className="rounded-xl border border-primary/30 bg-primary/5 p-6 text-center space-y-3"
        data-testid={`${testidPrefix}-summary`}
      >
        <Trophy className="w-10 h-10 text-primary mx-auto" />
        <h3 className="text-2xl font-serif text-text-primary">Série terminée</h3>
        <p className={`text-lg font-medium ${verdict.cls}`} data-testid={`${testidPrefix}-verdict`}>
          {verdict.label}
        </p>
        <p className="text-3xl font-serif text-text-primary">
          <span data-testid={`${testidPrefix}-final-score`}>{score.correct}/{score.total}</span>
          <span className="text-text-muted text-lg ml-2">· {pct}%</span>
        </p>
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button size="sm" onClick={restart} data-testid={`${testidPrefix}-restart`}>
            <RefreshCcw className="w-4 h-4 mr-1" /> Recommencer
          </Button>
          {onExit && (
            <Button size="sm" variant="outline" onClick={onExit} data-testid={`${testidPrefix}-exit`}>
              Retour
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Stable random shuffle of options using exercise id as seed. Computed
  // BEFORE any early-return so the React Hook is unconditionally called.
  // The shuffle is keyed by exercise id and matches via value (not index),
  // so correction stays exact (already declared earlier in this component).
  if (!current) return null;

  const display = current.display || {};

  return (
    <div
      className="rounded-xl border border-border bg-surface-elevated/30 p-5 space-y-4"
      data-testid={`${testidPrefix}-player`}
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Target className="w-3.5 h-3.5 text-primary" />
          <span>{pickFr(current.title) || 'Exercice'}</span>
          {current.difficulty && (
            <Badge variant="outline" className="text-[10px] uppercase">
              {current.difficulty}
            </Badge>
          )}
        </div>
        <div className="text-xs text-text-muted">
          <span data-testid={`${testidPrefix}-progress`}>{idx + 1}/{items.length}</span>
          {score.total > 0 && (
            <span className="ml-2" data-testid={`${testidPrefix}-score`}>
              · {score.correct}/{score.total}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full bg-background/60 rounded overflow-hidden">
        <div
          className="h-full bg-primary/80 transition-all"
          style={{ width: `${((idx + (answer ? 1 : 0)) / items.length) * 100}%` }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {/* Prompt */}
          <p className="text-sm text-text-primary">{pickFr(current.prompt)}</p>

          {/* Display */}
          <div className="rounded-lg bg-background/40 border border-border p-6 text-center min-h-[88px] flex flex-col items-center justify-center gap-3">
            {display.audioUrl ? (
              <>
                <button
                  type="button"
                  onClick={() => playAudio(display.audioUrl)}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
                    audioPlaying
                      ? 'bg-primary text-background'
                      : 'bg-primary/15 text-primary hover:bg-primary/25 border border-primary/40'
                  }`}
                  aria-label={audioPlaying ? 'Mettre en pause' : 'Écouter'}
                  data-testid={`${testidPrefix}-audio-play`}
                >
                  {audioPlaying ? <Pause className="w-7 h-7" /> : <Volume2 className="w-7 h-7" />}
                </button>
                <p className="text-xs text-text-muted">
                  Cliquez pour écouter, puis choisissez la bonne réponse.
                </p>
                {/* Reveal the arabic text only after answering */}
                {answer && display.arabicText && (
                  <span
                    className="font-arabic text-2xl text-primary mt-1"
                    dir="rtl"
                    data-testid={`${testidPrefix}-display-arabic`}
                  >
                    {display.arabicText}
                  </span>
                )}
                {answer && !display.arabicText && display.letter && (
                  <span
                    className="font-arabic text-5xl text-primary mt-1"
                    dir="rtl"
                    data-testid={`${testidPrefix}-display-letter`}
                  >
                    {display.letter}
                  </span>
                )}
              </>
            ) : display.letter ? (
              <span
                className="font-arabic text-6xl text-primary leading-none"
                dir="rtl"
                data-testid={`${testidPrefix}-display-letter`}
              >
                {display.letter}
              </span>
            ) : display.arabicText ? (
              <span
                className="font-arabic text-3xl sm:text-4xl text-primary leading-relaxed"
                dir="rtl"
                data-testid={`${testidPrefix}-display-arabic`}
              >
                {display.arabicText}
              </span>
            ) : null}
          </div>

          {/* Highlight chip if relevant */}
          {display.highlightedText && (
            <div className="flex items-center justify-center">
              <Badge className="bg-primary/15 text-primary border-primary/30">
                <Sparkles className="w-3 h-3 mr-1" />
                {display.highlightedText}
              </Badge>
            </div>
          )}

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {options.map((opt) => {
              const isPicked = answer?.value === opt.value;
              const isCorrect = opt.value === current.correctAnswer;
              let cls = 'border-border bg-background/40 hover:border-primary/40 hover:bg-primary/10';
              if (answer) {
                if (isCorrect) cls = 'border-emerald-500 bg-emerald-500/15 text-emerald-200';
                else if (isPicked) cls = 'border-rose-500 bg-rose-500/15 text-rose-200';
                else cls = 'border-border bg-background/40 opacity-60';
              }
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onChoose(opt)}
                  disabled={!!answer}
                  className={`text-sm px-4 py-3 rounded-md border transition-colors text-left ${cls}`}
                  data-testid={`${testidPrefix}-option-${opt.value}`}
                >
                  {pickFr(opt.label)}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {answer && (
            <div
              className="flex items-start gap-2 text-sm rounded-md p-3 border"
              data-testid={`${testidPrefix}-feedback`}
              style={{
                background: answer.correct ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
                borderColor: answer.correct ? 'rgba(16,185,129,0.35)' : 'rgba(244,63,94,0.35)',
              }}
            >
              {answer.correct ? (
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              ) : (
                <X className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
              )}
              <div className="flex-1">
                <p className={answer.correct ? 'text-emerald-300' : 'text-rose-300'}>
                  {answer.correct ? 'Bonne réponse !' : 'Pas tout à fait.'}
                </p>
                {current.explanation && (
                  <p className="text-text-secondary mt-0.5 text-xs">
                    {pickFr(current.explanation)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Record & Compare — only for audio exercises after answering */}
          {answer && display.audioUrl && (
            <RecordAndCompare
              key={current.id}
              ttsUrl={display.audioUrl}
              testidPrefix={`${testidPrefix}-rec`}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        {onExit ? (
          <Button variant="ghost" size="sm" onClick={onExit} data-testid={`${testidPrefix}-back`}>
            Retour
          </Button>
        ) : <span />}
        {answer && (
          <Button size="sm" onClick={next} data-testid={`${testidPrefix}-next`}>
            {idx + 1 < items.length ? 'Suivant' : 'Voir le résultat'}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ExerciseSeriesPlayer;
